import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import { membershipService, commonService } from '../../services/api';
import { getAcademicYearOptions } from '../../helpers/timeHelper';
import { PAYMENT_METHODS } from '../../helpers/subscriptionHelpers';

const CreateMembershipModal = ({ 
  show, 
  onHide, 
  userId, 
  editingMembership = null, 
  latestMembershipNumber = '',
  onMembershipCreated, 
  onMembershipUpdated 
}) => {
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Helper function to get default expiry date (31/12 of current year)
  const getDefaultExpiryDate = () => {
    const currentYear = new Date().getFullYear();
    return `${currentYear}-12-31`;
  };

  const [currentAcademicYear, setCurrentAcademicYear] = useState(new Date().getFullYear()); // Dynamic default value
  const [formData, setFormData] = useState({
    academicYear: currentAcademicYear, // Will be updated when currentAcademicYear loads
    amount: '',
    paidDate: getTodayDate(),
    paymentMethod: 'cash',
    expiryDate: getDefaultExpiryDate(),
    membershipNumber: latestMembershipNumber || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load current academic year from server
  useEffect(() => {
    const loadCurrentAcademicYear = async () => {
      try {
        const response = await commonService.getCurrentAcademicYear();
        if (response.success) {
          setCurrentAcademicYear(response.data);
          setFormData(prev => ({
            ...prev,
            academicYear: response.data
          }));
        } else {
          console.error('Error loading current academic year:', response.message);
          // Keep default value if error
        }
      } catch (err) {
        console.error('Error loading current academic year:', err);
        // Keep default value if error
      }
    };

    loadCurrentAcademicYear();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingMembership) {
      const formatDate = (dateString) => {
        if (!dateString) return '';
        if (typeof dateString === 'string' && dateString.includes('-')) {
          const datePart = dateString.split('T')[0];
          const parts = datePart.split('-');
          if (parts.length === 3 && parseInt(parts[0], 10) >= 1900) {
            return datePart;
          }
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime()) || date.getFullYear() < 1900) return '';
        return date.toISOString().split('T')[0];
      };

      setFormData({
        academicYear: editingMembership.academicYear || currentAcademicYear,
        amount: editingMembership.amount?.toString() || '',
        paidDate: formatDate(editingMembership.paidDate) || '',
        paymentMethod: editingMembership.paymentMethod || (editingMembership.paidDate ? 'cash' : ''),
        expiryDate: formatDate(editingMembership.expiryDate) || '',
        membershipNumber: editingMembership.membershipNumber || ''
      });
    } else {
      // Reset form for creation
      setFormData({
        academicYear: currentAcademicYear,
        amount: '',
        paidDate: getTodayDate(),
    paymentMethod: 'cash',
        expiryDate: getDefaultExpiryDate(),
        membershipNumber: latestMembershipNumber || ''
      });
    }
  }, [editingMembership, currentAcademicYear, latestMembershipNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'paidDate') {
      setFormData(prev => ({
        ...prev,
        paidDate: value,
        paymentMethod: value ? (prev.paymentMethod || 'cash') : ''
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.academicYear) {
      setError('Compilare tutti i campi obbligatori');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const membershipData = {
        amount: parseFloat(formData.amount),
        paidDate: formData.paidDate || null,
        paymentMethod: formData.paidDate ? (formData.paymentMethod || 'cash') : null,
        expiryDate: formData.expiryDate || null
      };

      let response;
      
      if (editingMembership) {
        // Update existing membership
        response = await membershipService.updateMembership(
          editingMembership.id, 
          {
            membershipNumber: formData.membershipNumber ? formData.membershipNumber.trim() : '',
            ...membershipData
          }
        );
      } else {
        // Create new membership
        const createData = {
          userId,
          academicYear: parseInt(formData.academicYear),
          membershipNumber: formData.membershipNumber ? formData.membershipNumber.trim() : null,
          ...membershipData
        };
        response = await membershipService.createMembership(createData);
      }

      if (response.success) {
        // Reset form
        setFormData({
          academicYear: currentAcademicYear,
          amount: '',
          paidDate: getTodayDate(),
    paymentMethod: 'cash',
          expiryDate: getDefaultExpiryDate(),
          membershipNumber: ''
        });
        
        // Notify parent component
        if (editingMembership && onMembershipUpdated) {
          onMembershipUpdated(response.data);
        } else if (!editingMembership && onMembershipCreated) {
          onMembershipCreated(response.data);
        }
        
        // Close modal
        onHide();
      } else {
        setError(response.message || `Errore nella ${editingMembership ? 'modifica' : 'creazione'} del tesseramento`);
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error creating membership:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setFormData({
        academicYear: currentAcademicYear,
        amount: '',
        paidDate: getTodayDate(),
    paymentMethod: 'cash',
        expiryDate: getDefaultExpiryDate(),
        membershipNumber: latestMembershipNumber || ''
      });
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editingMembership ? 'Modifica Tesseramento' : 'Nuovo Tesseramento'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="academicYear">
                <Form.Label>Anno Sportivo *</Form.Label>
                <Form.Select
                  name="academicYear"
                  value={formData.academicYear}
                  onChange={handleChange}
                  required
                  disabled={loading || editingMembership}
                >
                  {getAcademicYearOptions(currentAcademicYear).map(year => (
                    <option key={year} value={year}>
                      {year}/{year + 1}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3" controlId="membershipNumber">
                <Form.Label>Numero Tesseramento</Form.Label>
                <Form.Control
                  type="text"
                  name="membershipNumber"
                  value={formData.membershipNumber}
                  onChange={handleChange}
                  placeholder="Lasciare vuoto se provvisorio"
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="amount">
                <Form.Label>Importo (€) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="es. 150.00"
                  required
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3" controlId="expiryDate">
                <Form.Label>Data Scadenza</Form.Label>
                <Form.Control
                  type="date"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="paidDate">
                <Form.Label>Data Pagamento</Form.Label>
                <Form.Control
                  type="date"
                  name="paidDate"
                  value={formData.paidDate}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            {formData.paidDate && (
              <Col md={6}>
                <Form.Group className="mb-3" controlId="paymentMethod">
                  <Form.Label>Metodo Pagamento</Form.Label>
                  <Form.Select
                    name="paymentMethod"
                    value={formData.paymentMethod || 'cash'}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {PAYMENT_METHODS.map(pm => (
                      <option key={pm.value} value={pm.value}>{pm.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleClose}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
          >
            {loading && <Spinner animation="border" size="sm" className="me-2" />}
            {editingMembership ? 'Aggiorna Tesseramento' : 'Crea Tesseramento'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateMembershipModal;